/**
 * Pure parsing/validation for the Create School form (ADM-4, VIS-ADM-7/8).
 * No react-native import so vitest can cover it.
 *
 * Prices are typed in major units ("49", "49.00", "49,5") and converted to
 * integer cents here, with integer arithmetic only (no float multiply).
 * Empty or malformed input is invalid, never silently 0 or NaN.
 */

export const SCHOOL_CURRENCIES = ['USD', 'EUR', 'TRY'] as const;
export type SchoolCurrency = (typeof SCHOOL_CURRENCIES)[number];

const CURRENCY_SYMBOLS: Record<SchoolCurrency, string> = { USD: '$', EUR: '€', TRY: '₺' };

export const HARSHNESS_MIN = 0;
export const HARSHNESS_MAX = 10;
export const SLUG_MIN = 3;
export const SLUG_MAX = 50;

/** "49" -> 4900, "49.5" / "49,5" -> 4950, "0.1" -> 10. Returns null when invalid. */
export function parseMajorToCents(input: string): number | null {
  const text = (input ?? '').replace(/\s/g, '');
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(text);
  if (!match) return null;
  const whole = Number(match[1]);
  const frac = Number((match[2] ?? '').padEnd(2, '0'));
  const cents = whole * 100 + frac;
  return Number.isSafeInteger(cents) ? cents : null;
}

/** 4900 -> "49.00" (the editable major-unit form). */
export function centsToMajorInput(cents: number): string {
  const whole = Math.floor(cents / 100);
  const frac = String(cents % 100).padStart(2, '0');
  return `${whole}.${frac}`;
}

/** 4900, 'EUR' -> "€49.00". Plain formatting (no Intl, which varies under Hermes). */
export function formatCents(cents: number, currency: SchoolCurrency): string {
  return `${CURRENCY_SYMBOLS[currency]}${centsToMajorInput(cents)}`;
}

/** Whole numbers only ("10", " 10 "); "", "1.5", "-1", "1e3" are invalid. */
export function parseWholeNumber(input: string): number | null {
  const text = (input ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
}

export interface CreateSchoolFields {
  name: string;
  slug: string;
  maxStudents: string;
  harshness: string;
  currency: SchoolCurrency;
  basePrice: string;
  baseSeats: string;
  addonPrice: string;
  addonPackageSize: string;
  defaultLanguage: 'en' | 'tr' | null;
}

export type CreateSchoolField = Exclude<keyof CreateSchoolFields, 'currency' | 'defaultLanguage'>;

/** Field -> i18n key of the error message. */
export type CreateSchoolErrors = Partial<Record<CreateSchoolField, string>>;

export interface SchoolCreatePayload {
  name: string;
  slug: string;
  subscription_type: 'b2b2c';
  max_students: number;
  harshness_scale: number;
  system_prompt_config: Record<string, never>;
}

export interface SchoolBillingPayload {
  billing_currency: SchoolCurrency;
  billing_base_price_cents: number;
  billing_base_seats: number;
  billing_addon_package_size: number;
  billing_addon_price_cents: number;
  default_language?: 'en' | 'tr';
}

export type CreateSchoolValidation =
  | { ok: true; school: SchoolCreatePayload; billing: SchoolBillingPayload }
  | { ok: false; errors: CreateSchoolErrors };

/** Mirrors the backend rules (SchoolCreate validators in schools.py). */
export function validateCreateSchoolForm(fields: CreateSchoolFields): CreateSchoolValidation {
  const errors: CreateSchoolErrors = {};
  const name = fields.name.trim();
  const slug = fields.slug.trim().toLowerCase();

  if (!name) errors.name = 'createSchool.errors.nameRequired';
  if (!slug) errors.slug = 'createSchool.errors.slugRequired';
  else if (!/^[a-z0-9_-]+$/.test(slug) || slug.length < SLUG_MIN || slug.length > SLUG_MAX) {
    errors.slug = 'createSchool.errors.slugFormat';
  }

  const maxStudents = parseWholeNumber(fields.maxStudents);
  if (maxStudents === null || maxStudents < 1) errors.maxStudents = 'createSchool.validationMaxStudents';

  const harshness = parseWholeNumber(fields.harshness);
  if (harshness === null || harshness < HARSHNESS_MIN || harshness > HARSHNESS_MAX) {
    errors.harshness = 'createSchool.validationHarshness';
  }

  const basePrice = parseMajorToCents(fields.basePrice);
  if (basePrice === null) errors.basePrice = 'createSchool.errors.price';

  const baseSeats = parseWholeNumber(fields.baseSeats);
  if (baseSeats === null || baseSeats < 1) errors.baseSeats = 'createSchool.errors.positiveWhole';

  const addonPrice = parseMajorToCents(fields.addonPrice);
  if (addonPrice === null) errors.addonPrice = 'createSchool.errors.price';

  const addonPackageSize = parseWholeNumber(fields.addonPackageSize);
  if (addonPackageSize === null || addonPackageSize < 1) errors.addonPackageSize = 'createSchool.errors.positiveWhole';

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    school: {
      name,
      slug,
      subscription_type: 'b2b2c',
      max_students: maxStudents!,
      harshness_scale: harshness!,
      system_prompt_config: {},
    },
    billing: {
      billing_currency: fields.currency,
      billing_base_price_cents: basePrice!,
      billing_base_seats: baseSeats!,
      billing_addon_package_size: addonPackageSize!,
      billing_addon_price_cents: addonPrice!,
      ...(fields.defaultLanguage ? { default_language: fields.defaultLanguage } : {}),
    },
  };
}

/** Readable text from an apiRequest error (a 422 `detail` is a list of {msg}). */
export function apiErrorText(error: unknown, fallback: string): string {
  const err = error as { detail?: unknown; message?: unknown } | null | undefined;
  const detail = err?.detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof d === 'object' ? (d as { msg?: unknown }).msg : d))
      .filter((m): m is string => typeof m === 'string' && m.length > 0);
    if (msgs.length) return msgs.join('; ');
  }
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err?.message === 'string' && err.message && err.message !== '[object Object]') return err.message;
  return fallback;
}
